import { useCallback, useState, useEffect } from 'react';
import { CHARACTER_LIMITS, FILE_UPLOAD, FORM_FIELDS } from '../../constants/form-constants';
import { fetchCategories } from '../../../../services/category/category.service';
import { CategoryType, PROGRAM_CATEGORY, PROGRAM_CATEGORY_LABELS } from '../../../../services/constants';

const useAddProgramHook = (handleBackToList, handleAddNew, handleEditProgram, isEditingMode, programData) => {
  const [categories, setCategories] = useState([]);

  const extractFileNameFromUrl = (url, id, defaultName) => {
    if (!url) {
      return defaultName;
    }
    const parts = url.split('/');
    let fileName = parts[parts.length - 1];
    fileName = fileName.split(/[?#]/)[0];
    if (id && fileName.startsWith(`${id}_`)) {
      fileName = fileName.replace(`${id}_`, '');
    }
    fileName = fileName.replace(/(_\d{10,}|\d+x)$/g, '');
    const fileParts = fileName.split('_');
    if (fileParts.length >= 1 && fileParts[fileParts.length - 1].includes('.')) {
      return fileName;
    }
  };


  // Helper function to get category name from category object
  const getCategoryName = (category) => {
    if (!category) return '';
    // Handle new structure: {id, name, type}
    if (typeof category === 'object' && category.name) {
      return category.name;
    }
    // Handle old structure: string
    if (typeof category === 'string') {
      return category;
    }
    return '';
  };

  const getCategoryByType = useCallback((metadata, categoryType) => {
    if (!metadata || !Array.isArray(metadata.categories)) {
      return '';
    }
    const category = metadata.categories.find(cat => cat?.type === categoryType);
    return category ? getCategoryName(category) : '';
  }, []);

  // Helper function to map API category name to PROGRAM_CATEGORY constant value
  const mapCategoryNameToConstant = (categoryName) => {
    if (!categoryName) return '';
    
    // Create reverse mapping from label to constant
    const labelToConstant = Object.entries(PROGRAM_CATEGORY_LABELS).reduce((acc, [constant, label]) => {
      acc[label] = constant;
      return acc;
    }, {});

    // Try exact match first
    if (labelToConstant[categoryName]) {
      return labelToConstant[categoryName];
    }

    // Try case-insensitive match
    const normalizedName = categoryName.trim();
    const matchedEntry = Object.entries(labelToConstant).find(
      ([label]) => label.toLowerCase() === normalizedName.toLowerCase()
    );
    
    if (matchedEntry) {
      return matchedEntry[1];
    }

    // If it's already a constant value (uppercase), return as is
    if (Object.values(PROGRAM_CATEGORY).includes(categoryName.toUpperCase())) {
      return categoryName.toUpperCase();
    }

    return '';
  };

  const getProgramCategoryName = useCallback((metadata) => {
    if (metadata?.category) {
      const categoryName = getCategoryName(metadata.category);
      return mapCategoryNameToConstant(categoryName);
    }
    const serviceCategoryName = getCategoryByType(metadata, CategoryType.SERVICE);
    return mapCategoryNameToConstant(serviceCategoryName);
  }, [getCategoryByType]);

  // Helper function to get BEST category name from metadata
  const getBestCategoryName = (metadata) => {
    return getCategoryByType(metadata, CategoryType.BEST);
  };

  const initialFormData = {
    [FORM_FIELDS.DISPLAY_NAME_EN]: isEditingMode ? programData?.titleEN : '',
    [FORM_FIELDS.DISPLAY_NAME_ES]: isEditingMode ? programData?.titleES : '',
    [FORM_FIELDS.SHORT_DESCRIPTION_EN]: isEditingMode ? programData?.descriptionShortEN : '',
    [FORM_FIELDS.SHORT_DESCRIPTION_ES]: isEditingMode ? programData?.descriptionShortES : '',
    [FORM_FIELDS.LONG_DESCRIPTION_EN]: isEditingMode ? programData?.descriptionLongEN : '',
    [FORM_FIELDS.LONG_DESCRIPTION_ES]: isEditingMode ? programData?.descriptionLongES : '',
    [FORM_FIELDS.THUMBNAIL_IMAGE]: isEditingMode ? programData?.thumbnailUrl : null,
    [FORM_FIELDS.THUMBNAIL_IMAGE_NAME]: isEditingMode ? extractFileNameFromUrl(programData?.thumbnailUrl, programData?.id, programData?.name): '',
    [FORM_FIELDS.PROGRAM_LANGUAGE]: isEditingMode ? programData?.metadata?.primaryLanguage: '',
    [FORM_FIELDS.CERTIFICATE_ELIGIBILITY]: isEditingMode ? programData?.metadata?.certificateEligible : false,
    [FORM_FIELDS.CONTENT_PROVIDER]: isEditingMode ? programData?.metadata?.contentProvider : '',
    [FORM_FIELDS.CONTENT_DELIVERY_METHOD]: isEditingMode ? programData?.metadata?.contentDeliveryMethod : '',
    [FORM_FIELDS.PROGRAM_CATEGORY]: isEditingMode ? getProgramCategoryName(programData?.metadata) : '',
    [FORM_FIELDS.BEST_CATEGORY]: isEditingMode ? getBestCategoryName(programData?.metadata) : '',
    [FORM_FIELDS.COURSE_DURATION]: isEditingMode ? programData?.metadata?.courseDuration : '',
    [FORM_FIELDS.COURSES]: isEditingMode ? programData?.courses : [],
    showDeleteDialog: false,
    showDeliveryMethodChangeDialog: false,
    showContentProviderChangeDialog: false
  };

  const [formData, setFormDataRaw] = useState(initialFormData);
  const [pendingContentDeliveryMethod, setPendingContentDeliveryMethod] = useState(null);
  const [pendingContentProvider, setPendingContentProvider] = useState(null);

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const checkIfCoursesHasChangedFromInitialState = (programData, data) => {
    const original = programData?.courses || [];
    const current = data?.courses || [];

    if (original.length !== current.length) return true;

    return !original.every((origCourse, index) => {
      const currCourse = current[index];
      return origCourse.id === currCourse.id && origCourse.displayOrder === currCourse.displayOrder;
    });
  };

  const validateForm = useCallback((data) => {
    try {
      // Check all required fields are present and not empty
      if (!data.displayNameEn?.trim() || data.displayNameEn.length > 100) return false;
      if (!data.displayNameEs?.trim() || data.displayNameEs.length > 100) return false;
      if (!data.shortDescriptionEn?.trim() || data.shortDescriptionEn.length > 300) return false;
      if (!data.shortDescriptionEs?.trim() || data.shortDescriptionEs.length > 300) return false;
      if (!data.longDescriptionEn?.trim() || data.longDescriptionEn.length > 1000) return false;
      if (!data.longDescriptionEs?.trim() || data.longDescriptionEs.length > 1000) return false;
      if (!data.thumbnailImage) return false;
      if (!data.programLanguage) return false;
      if (!data.contentDeliveryMethod) return false;
      if (!data.contentProvider) return false;
      if (!data.programCategory) return false;
      if (!data.courseDuration) return false;
      if (!data.courses.length) return false;

      if (isEditingMode) {
        // Get the original program category constant value for comparison
        const originalProgramCategory = getProgramCategoryName(programData?.metadata);
        
        return data.displayNameEn !== programData?.titleEN ||
          data.displayNameEs !== programData?.titleES ||
          data.shortDescriptionEn !== programData?.descriptionShortEN ||
          data.shortDescriptionEs !== programData?.descriptionShortES ||
          data.longDescriptionEn !== programData?.descriptionLongEN ||
          data.longDescriptionEs !== programData?.descriptionLongES ||
          data.programLanguage !== programData?.metadata?.primaryLanguage ||
          data.certificateEligibility !== programData?.metadata?.certificateEligible ||
          data.contentProvider !== programData?.metadata?.contentProvider ||
          data.contentDeliveryMethod !== programData?.metadata?.contentDeliveryMethod ||
          data.programCategory !== originalProgramCategory ||
          data.courseDuration !== programData?.metadata?.courseDuration ||
          (data.thumbnailImage instanceof File) ||
          checkIfCoursesHasChangedFromInitialState(programData, data);
      }

      return true;
    } catch (error) {
      return false;
    }
  }, [getProgramCategoryName, isEditingMode, programData]);

  const setFormData = useCallback((newData) => {
    setFormDataRaw(newData);
    setIsFormValid(validateForm(newData));
  }, [validateForm]);

  const handleChange = useCallback((e) => {
    const { name, type, checked } = e.target;
    let value = type === 'checkbox' ? checked : e.target.value;

    if (name.includes('displayName')) {
      value = value.slice(0, CHARACTER_LIMITS.DISPLAY_NAME);
    } else if (name.includes('shortDescription')) {
      value = value.slice(0, CHARACTER_LIMITS.SHORT_DESCRIPTION);
    } else if (name.includes('longDescription')) {
      value = value.slice(0, CHARACTER_LIMITS.LONG_DESCRIPTION);
    }

    if (name === FORM_FIELDS.CONTENT_DELIVERY_METHOD) {
      if (formData.contentDeliveryMethod && value !== formData.contentDeliveryMethod && formData.courses.length > 0) {
        setPendingContentDeliveryMethod(value);
        setFormData(prev => ({
          ...prev,
          showDeliveryMethodChangeDialog: true,
        }));
        return;
      }
      const newFormData = {
        ...formData,
        [FORM_FIELDS.CONTENT_DELIVERY_METHOD]: value,
        [FORM_FIELDS.CONTENT_PROVIDER]: '',
        [FORM_FIELDS.COURSES]: [],
      };
      setFormData(newFormData);
      return;
    }

    if (name === FORM_FIELDS.CONTENT_PROVIDER) {
      if (formData.contentProvider && value !== formData.contentProvider && formData.courses.length > 0) {
        setPendingContentProvider(value);
        setFormData(prev => ({
          ...prev,
          showContentProviderChangeDialog: true,
          [name]: formData.contentProvider
        }));
        return;
      }
      const newFormData = {
        ...formData,
        [name]: value,
        [FORM_FIELDS.COURSES]: [],
      };
      setFormData(newFormData);
      return;
    }

    const newFormData = {
      ...formData,
      [name]: value
    };

    setFormData(newFormData);
    setIsFormValid(validateForm(newFormData));
  }, [formData, setFormData, validateForm]);

  const validateImageFile = (file) => {
    if (!file) return false;

    if (!FILE_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
      setAlertState({
        open: true,
        message: FILE_UPLOAD.ERROR_MESSAGES.INVALID_TYPE,
        severity: 'error'
      });
      return false;
    }

    if (file.size > FILE_UPLOAD.MAX_SIZE) {
      setAlertState({
        open: true,
        message: 'Image file size should not exceed 1 MB',
        severity: 'error'
      });
      return false;
    }

    return true;
  };

  const handleImageUpload = async (input) => {
    let file;
    if (input instanceof File) {
      file = input;
    } else if (input?.preventDefault) {
      input.preventDefault();
      file = input.dataTransfer ? input.dataTransfer.files[0] : input.target.files[0];
    }

    // Validate file type and size first
    if (!validateImageFile(file)) return;

    // Then validate image dimensions
    try {
      await validateImageDimensions(file);

      const newFormData = {
        ...formData,
        thumbnailImage: file,
        thumbnailImageName: file.name,
      };

      setFormData(newFormData);
      setIsFormValid(validateForm(newFormData));

    } catch (error) {
      setAlertState({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (img.width < FILE_UPLOAD.MIN_WIDTH) {
          reject(new Error(FILE_UPLOAD.ERROR_MESSAGES.MIN_WIDTH));
        } else {
          resolve();
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = objectUrl;
    });
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    await handleImageUpload(file);
  };

  const handleAlertClose = () => {
    setAlertState({
      ...alertState,
      open: false
    });
  };

  const handleImageDelete = (confirmed) => {
    if (confirmed === undefined || confirmed === null) {
      setFormData(prev => ({
        ...prev,
        showDeleteDialog: true
      }));
      return;
    }

    if (confirmed) {
      const newFormData = {
        ...formData,
        thumbnailImage: null,
        thumbnailImageName: '',
        showDeleteDialog: false
      };
      setFormData(newFormData);
      setIsFormValid(validateForm(newFormData));
    } else {
      setFormData(prev => ({
        ...prev,
        showDeleteDialog: false
      }));
    }
  };

  const handleContentDeliveryMethodChange = (confirmed) => {
    if (confirmed && Boolean(pendingContentDeliveryMethod)) {
      const newFormData = {
        ...formData,
        [FORM_FIELDS.CONTENT_DELIVERY_METHOD]: pendingContentDeliveryMethod,
        [FORM_FIELDS.CONTENT_PROVIDER]: '',
        [FORM_FIELDS.COURSES]: [],
        showDeliveryMethodChangeDialog: false,
        showContentProviderChangeDialog: false
      };
      setFormData(newFormData);
    } else {
      setFormData(prev => ({
        ...prev,
        showDeliveryMethodChangeDialog: false
      }));
    }
    setPendingContentDeliveryMethod(null);
  };

  const handleContentProviderChange = (confirmed) => {
    if (confirmed && Boolean(pendingContentProvider)) {
      handleCourseChange([]);
      const newFormData = {
        ...formData,
        [FORM_FIELDS.CONTENT_PROVIDER]: pendingContentProvider,
        [FORM_FIELDS.COURSES]: [],
        showContentProviderChangeDialog: false
      };
      setFormDataRaw(newFormData);
      setIsFormValid(validateForm(newFormData));
    } else {
      setFormData(prev => ({
        ...prev,
        showContentProviderChangeDialog: false,
        [FORM_FIELDS.CONTENT_PROVIDER]: formData.contentProvider
      }));
    }
    setPendingContentProvider(null);
  };

  const appendThumbnailToFormData = async (formDataToSend) => {
    formDataToSend.append(typeof formData.thumbnailImage === 'string' ? 'thumbnailUri' : 'thumbnail' , formData.thumbnailImage);
  };

  const appendCoursesToFormData = async (formDataToSend) => {
    if (!formData.courses || formData.courses.length === 0) return;

    formData.courses.forEach((course, index) => {
      formDataToSend.append(`courses[${index}].id`, course.id);
      formDataToSend.append(`courses[${index}].displayOrder`, course.displayOrder);
    });
  };

  const getCategoryIdByNameAndType = (categoryName, categoryType) => {
    if (!categoryName || !Array.isArray(categories) || categories.length === 0) {
      return null;
    }
    const category = categories.find(cat => cat?.name === categoryName && cat?.type === categoryType);
    return category?.id || null;
  };

  const appendProgramCategoryToFormData = (formDataToSend, formData) => {
    if (formData.programCategory) {
      // Validate that the category is a valid PROGRAM_CATEGORY constant
      const validCategories = Object.values(PROGRAM_CATEGORY);
      if (validCategories.includes(formData.programCategory)) {
        // Send the constant value directly (e.g., "EDUCATION", "WORKFORCE", etc.)
        formDataToSend.append('metadata.category', formData.programCategory);
      }
    }
  };

  const appendBESTCategoryIdToFormData = (formDataToSend, formData) => {
    if (formData.bestCategory) {
      const bestCategoryId = getCategoryIdByNameAndType(formData.bestCategory, CategoryType.BEST);
      if (bestCategoryId) {
        formDataToSend.append('metadata.bestCategoryId', bestCategoryId);
      }
    }
  };


  const prepareFormData = async () => {
    const formDataToSend = new FormData();
    formDataToSend.append('titleEN', formData.displayNameEn);
    formDataToSend.append('titleES', formData.displayNameEs);
    formDataToSend.append('descriptionShortEN', formData.shortDescriptionEn);
    formDataToSend.append('descriptionShortES', formData.shortDescriptionEs);
    formDataToSend.append('descriptionLongEN', formData.longDescriptionEn);
    formDataToSend.append('descriptionLongES', formData.longDescriptionEs);
    formDataToSend.append('metadata.certificateEligible', formData.certificateEligibility);

    appendProgramCategoryToFormData(formDataToSend, formData);
    appendBESTCategoryIdToFormData(formDataToSend, formData);

    formDataToSend.append('metadata.courseDuration', formData.courseDuration);
    formDataToSend.append('metadata.contentProvider', formData.contentProvider);
    formDataToSend.append('metadata.primaryLanguage', formData.programLanguage.toUpperCase());
    formDataToSend.append('metadata.contentDeliveryMethod', formData.contentDeliveryMethod);

    appendCoursesToFormData(formDataToSend);
    appendThumbnailToFormData(formDataToSend);
    return formDataToSend;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    const formDataToSend = await prepareFormData();
    if (isEditingMode) {
      await handleEditProgram(formDataToSend, programData?.id)
    } else {
      await handleAddNew(formDataToSend);
    }
  };

  const getImageUrl = useCallback((thumbnailImage) => {
    if (thumbnailImage instanceof File) {
      return typeof URL?.createObjectURL === 'function' ? URL.createObjectURL(thumbnailImage) : '';
    }
    return thumbnailImage?.name || thumbnailImage || '';
  }, []);

  const handleCourseChange = useCallback((courses) => {
    setFormDataRaw(prevFormData => {
      const newFormData = {
        ...prevFormData,
        courses: courses || [],
      };
      setIsFormValid(validateForm(newFormData));
      return newFormData;
    });
  }, [validateForm])

  // Function to fetch categories
  /**
   * Fetches categories from the API and updates state
   * @async
   * @returns {Promise<void>}
   */
  const getCategories = async () => {
    try {
      const res = await fetchCategories();
      const categoriesList = res?.data || res?.payload || res;
      setCategories(Array.isArray(categoriesList) ? categoriesList : []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  return {
    formData,
    setFormData,
    alertState,
    setAlertState,
    isFormValid,
    categories,
    handleChange,
    handleImageUpload,
    handleAlertClose,
    handleImageDelete,
    prepareFormData,
    handleSubmit,
    getImageUrl,
    handleDrop,
    handleCourseChange,
    checkIfCoursesHasChangedFromInitialState,
    handleContentDeliveryMethodChange,
    handleContentProviderChange
  };
};

export default useAddProgramHook;
